import React, { useState, useEffect } from "react";
import { submitContactForm, fetchWebsiteSettings } from "../api";
import { useWebsiteSettings } from "../contexts/WebsiteSettingsContext";

export default function Contact() {
  const { settings, isLoading } = useWebsiteSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    type: null,
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all fields.",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    const result = await submitContactForm(formData);

    if (result.success) {
      setSubmitStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully.",
      });

      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } else {
      setSubmitStatus({
        type: "error",
        message: result.error || "Failed to send message. Please try again.",
      });
    }
    setIsSubmitting(false);
  };
  if (isLoading) return null;

  return (
    <div className="w-full">
      {/* Contact Info & Form */}
      <section className="py-5 mt-4 px-4 md:px-20 grid md:grid-cols-2 gap-12">
        {/* Contact Info */}
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold">Reach Us</h3>
          {settings.address && (
            <p>
              {settings.address.address}, {settings.address.city},{" "}
              {settings.address.state} - {settings.address.pincode}
            </p>
          )}
          <a
            href="https://www.google.com/maps"
            target="_blank"
            rel="noopener noreferrer"
            className="text-red-600 hover:underline"
          >
            Get Directions
          </a>
          {settings.email && <p>Email: {settings.email}</p>}
          <h5 className="font-bold mt-4 mb-2">Social Links</h5>
          <ul className="flex space-x-4">
            {settings?.facebook && (
              <li>
                <a
                  href={settings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black fs-5 hover:text-red-600"
                >
                  <i className="bi bi-facebook text-2xl"></i>
                </a>
              </li>
            )}
            {settings?.instagram && (
              <li>
                <a
                  href={settings.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black fs-5 hover:text-red-600"
                >
                  <i className="bi bi-instagram text-2xl"></i>
                </a>
              </li>
            )}
            {settings?.linkedin && (
              <li>
                <a
                  href={settings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black fs-5 hover:text-red-600"
                >
                  <i className="bi bi-linkedin text-2xl"></i>
                </a>
              </li>
            )}
            {settings?.twitter && (
              <li>
                <a
                  href={settings.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black fs-5 hover:text-red-600"
                >
                  <i className="bi bi-twitter text-2xl"></i>
                </a>
              </li>
            )}
            {settings?.youtube && (
              <li>
                <a
                  href={settings.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black fs-5 hover:text-red-600"
                >
                  <i className="bi bi-youtube text-2xl"></i>
                </a>
              </li>
            )}
          </ul>
        </div>

        {/* Contact Form */}
        <div>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                placeholder="Your Name"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                placeholder="Your Email"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-32 focus:outline-none focus:ring-2 focus:ring-red-600"
                placeholder="Your Message"
                required
              ></textarea>
            </div>

            {/* Status Message */}
            {submitStatus.type && (
              <div
                className={`p-3 rounded-md ${
                  submitStatus.type === "success"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-theme-color text-white px-6 py-3 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
